
import { useState } from "react";
import { useForm } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import LogoUpload from "@/components/ui/logo-upload";

import { useToast } from "@/hooks/use-toast";
import { Organization, OrganizationFormData } from "@/types/organization";
import { Building2, Palette, Settings } from "lucide-react";

interface OrganizationFormProps {
  onSuccess: (createdOrgId?: string) => void;
  onCancel: () => void;
}

const OrganizationForm = ({ onSuccess, onCancel }: OrganizationFormProps) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<OrganizationFormData>({
    defaultValues: {
      subdomain: '',
      name: '',
      display_name: '',
      logo_url: '',
      is_active: true,
    }
  });

  const isActive = watch('is_active');

  const validateSubdomain = (subdomain: string) => {
    const subdomainRegex = /^[a-z0-9-]+$/;
    if (!subdomainRegex.test(subdomain)) {
      return "Subdomain can only contain lowercase letters, numbers, and hyphens";
    }
    if (subdomain.length < 2) {
      return "Subdomain must be at least 2 characters long";
    }
    if (subdomain.startsWith('-') || subdomain.endsWith('-')) {
      return "Subdomain cannot start or end with a hyphen";
    }
    return true;
  };

  const onSubmit = async (data: OrganizationFormData) => {
    setIsSubmitting(true);
    
    try {
      // Validate subdomain format
      const subdomainValidation = validateSubdomain(data.subdomain);
      if (subdomainValidation !== true) {
        toast({
          title: "Invalid Subdomain",
          description: subdomainValidation,
          variant: "destructive",
        });
        return;
      }

      // Create new organization
      const { data: newOrg, error } = await supabase
        .from('organizations')
        .insert({
          subdomain: data.subdomain,
          name: data.name,
          display_name: data.display_name || null,
          logo_url: data.logo_url || null,
          is_active: data.is_active,
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          toast({
            title: "Subdomain Already Exists",
            description: "This subdomain is already taken. Please choose a different one.",
            variant: "destructive",
          });
          return;
        }
        throw error;
      }

      // Copy default parcel types to the new organization
      try {
        const { error: parcelTypesError } = await supabase.rpc('copy_default_parcel_types_to_org', {
          target_org_id: newOrg.id
        });

        if (parcelTypesError) {
          console.warn('Warning: Failed to copy default parcel types:', parcelTypesError);
        }
      } catch (parcelTypesError) {
        console.warn('Warning: Failed to copy default parcel types:', parcelTypesError);
      }

      toast({
        title: "Success",
        description: "Organization created successfully. Redirecting to management page...",
      });

      onSuccess(newOrg.id);
    } catch (error) {
      console.error('Organization form error:', error);
      toast({
        title: "Error",
        description: "Failed to create organization",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Content Area - takes remaining space minus footer */}
      <div className="flex-1 min-h-0 p-6">
        <form id="organization-form" onSubmit={handleSubmit(onSubmit)} className="h-full" role="form" aria-label="Create organization form">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
            <div className="border-b border-border mb-6">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="basic" className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm">
                  <Building2 className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Basic Info</span>
                  <span className="sm:hidden">Basic</span>
                </TabsTrigger>
                <TabsTrigger value="settings" className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm">
                  <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>Settings</span>
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="flex-1 overflow-y-auto min-h-0">
              <TabsContent value="basic" className="space-y-6 mt-0 pb-6 animate-fade-in min-h-[400px]">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="subdomain">Subdomain *</Label>
                    <Input
                      id="subdomain"
                      {...register('subdomain', { 
                        required: 'Subdomain is required',
                        validate: validateSubdomain
                      })}
                      placeholder="company-name"
                      aria-invalid={errors.subdomain ? 'true' : 'false'}
                      aria-describedby={errors.subdomain ? 'subdomain-error' : 'subdomain-help'}
                    />
                    {errors.subdomain && (
                      <p id="subdomain-error" className="text-sm text-destructive" role="alert">{errors.subdomain.message}</p>
                    )}
                    <p id="subdomain-help" className="text-xs text-muted-foreground">
                      Will be used as: {watch('subdomain')}.yourapp.com
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="name">Internal Name *</Label>
                    <Input
                      id="name"
                      {...register('name', { required: 'Name is required' })}
                      placeholder="Company Name Inc"
                      aria-invalid={errors.name ? 'true' : 'false'}
                      aria-describedby={errors.name ? 'name-error' : undefined}
                    />
                    {errors.name && (
                      <p id="name-error" className="text-sm text-destructive" role="alert">{errors.name.message}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="display_name">Display Name</Label>
                  <Input
                    id="display_name"
                    {...register('display_name')}
                    placeholder="Company Name (shown to users)"
                    aria-describedby="display-name-help"
                  />
                  <p id="display-name-help" className="text-xs text-muted-foreground">
                    If empty, the internal name will be used
                  </p>
                </div>

                <div className="space-y-2">
                  <LogoUpload
                    onLogoChange={(url) => setValue('logo_url', url || '')}
                    currentLogo={watch('logo_url')}
                    organizationName={watch('display_name') || watch('name') || 'Organization'}
                    disabled={isSubmitting}
                  />
                  
                  <div className="pt-2 border-t border-muted">
                    <Label htmlFor="logo_url" className="text-xs text-muted-foreground">
                      Or enter logo URL manually:
                    </Label>
                    <Input
                      id="logo_url"
                      {...register('logo_url')}
                      placeholder="https://example.com/logo.png"
                      type="url"
                      className="mt-1"
                      aria-describedby="logo-url-help"
                    />
                    <p id="logo-url-help" className="text-xs text-muted-foreground mt-1">
                      Optional: Provide a direct URL to your organization's logo
                    </p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="settings" className="space-y-6 mt-0 pb-6 animate-fade-in min-h-[400px]">
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_active"
                      checked={isActive}
                      onCheckedChange={(checked) => setValue('is_active', checked)}
                      aria-describedby="active-help"
                    />
                    <Label htmlFor="is_active">Organization is active</Label>
                    <p id="active-help" className="sr-only">
                      Toggle to enable or disable this organization
                    </p>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Inactive organizations cannot be accessed by users but remain in the system.
                  </p>
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </form>
      </div>
      
      <div className="flex justify-end space-x-2 p-6 pt-4 border-t bg-background">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={isSubmitting} 
          aria-describedby="submit-help"
          form="organization-form"
        >
          {isSubmitting ? 'Creating...' : 'Create Organization'}
        </Button>
        <p id="submit-help" className="sr-only">
          Create a new organization with the provided information
        </p>
      </div>
    </div>
  );
};

export default OrganizationForm;
